/** @format */

export interface MarkdownRun {
	value: string;
	isBold: boolean;
	isItalic: boolean;
}

const isWordChar = (ch: string | undefined): boolean => ch !== undefined && /[\p{L}\p{N}]/u.test(ch);

const isWhiteSpace = (ch: string | undefined): boolean => ch !== undefined && /\s/.test(ch);

const asteriskRunLength = (input: string, start: number): number => {
	let length = 1;
	while (input[start + length] === "*") {
		length += 1;
	}
	return length;
};

const canOpenEmphasis = (input: string, start: number, runLength: number): boolean => {
	if (runLength > 3) {
		return false;
	}
	const after = input[start + runLength];
	if (after === undefined || isWhiteSpace(after)) {
		return false;
	}
	return !(isWordChar(input[start - 1]) && isWordChar(after));
};

const canCloseEmphasis = (input: string, index: number, runLength: number): boolean => {
	const before = input[index - 1];
	if (isWhiteSpace(before)) {
		return false;
	}
	return !(isWordChar(before) && isWordChar(input[index + runLength]));
};

const findEmphasisClose = (input: string, start: number, runLength: number): number => {
	let i = start;
	while (i < input.length) {
		if (input[i] === "\\" && input[i + 1] === "*") {
			i += 2;
			continue;
		}
		if (input[i] === "*") {
			const closeLength = asteriskRunLength(input, i);
			if (closeLength === runLength && canCloseEmphasis(input, i, closeLength)) {
				return i;
			}
			i += closeLength;
			continue;
		}
		i += 1;
	}
	return -1;
};

const unescapeAsterisks = (value: string): string => value.replace(/\\\*/g, "*");

export const parseMarkdownRuns = (input: string): MarkdownRun[] => {
	const runs: MarkdownRun[] = [];
	let text = "";
	let i = 0;

	const flushText = () => {
		if (text) {
			runs.push({ value: text, isBold: false, isItalic: false });
			text = "";
		}
	};

	while (i < input.length) {
		const ch = input[i];
		if (ch === "\\" && input[i + 1] === "*") {
			text += "*";
			i += 2;
			continue;
		}

		if (ch === "*") {
			const runLength = asteriskRunLength(input, i);
			const closeIndex = canOpenEmphasis(input, i, runLength) ? findEmphasisClose(input, i + runLength, runLength) : -1;
			if (closeIndex === -1) {
				text += input.slice(i, i + runLength);
				i += runLength;
				continue;
			}
			flushText();
			runs.push({
				value: unescapeAsterisks(input.slice(i + runLength, closeIndex)),
				isBold: runLength >= 2,
				isItalic: runLength !== 2,
			});
			i = closeIndex + runLength;
			continue;
		}
		text += ch;
		i += 1;
	}
	flushText();
	return runs;
};

export const containsMarkdown = (input: string): boolean => {
	const runs = parseMarkdownRuns(input);
	return runs.some((run) => run.isBold || run.isItalic) || runs.map((run) => run.value).join("") !== input;
};
